import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { theme } from '../styles/theme';

const FooterContainer = styled.footer`
  background: linear-gradient(135deg, ${theme.colors.backgroundDark} 0%, ${theme.colors.backgroundLight} 100%);
  padding: ${theme.spacing(6)} ${theme.spacing(2)};
  margin-top: ${theme.spacing(6)};
  border-top: 1px solid ${theme.colors.outline};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent 0%, ${theme.colors.primary} 50%, transparent 100%);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing(4)} ${theme.spacing(1)};
    margin-top: ${theme.spacing(4)};
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const ImportantInfo = styled.div`
  background: ${theme.colors.surface};
  padding: ${theme.spacing(3)};
  border-radius: ${theme.shape.borderRadius}px;
  border-left: 4px solid ${theme.colors.primary};
  margin-bottom: ${theme.spacing(3)};
  box-shadow: ${theme.elevation[2]};

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing(2)};
  }
`;

const InfoTitle = styled.h3`
  margin: 0 0 ${theme.spacing(2)} 0;
  color: ${theme.colors.primary};
  font-size: ${theme.typography.h5.fontSize};
  font-weight: ${theme.typography.h5.fontWeight};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.typography.h6.fontSize};
  }
`;

const InfoText = styled.p`
  margin: 0 0 ${theme.spacing(1)} 0;
  color: ${theme.colors.onSurface};
  line-height: ${theme.typography.body1.lineHeight};
  font-size: ${theme.typography.body1.fontSize};

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.typography.body2.fontSize};
  }
`;

const ServiceHours = styled.div`
  background: ${theme.colors.primary};
  color: ${theme.colors.onPrimary};
  padding: ${theme.spacing(3)};
  border-radius: ${theme.shape.borderRadius}px;
  text-align: center;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing(2)};
  }
`;

const ServiceTitle = styled.h3`
  margin: 0 0 ${theme.spacing(2)} 0;
  font-size: ${theme.typography.h5.fontSize};
  font-weight: ${theme.typography.h5.fontWeight};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.typography.h6.fontSize};
  }
`;

const ServiceText = styled.p`
  margin: 0;
  font-size: ${theme.typography.body1.fontSize};
  line-height: ${theme.typography.body1.lineHeight};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.typography.body2.fontSize};
  }
`;

const AllergenInfo = styled.div`
  margin-top: ${theme.spacing(3)};
  padding: ${theme.spacing(2)};
  background: ${theme.colors.surfaceVariant};
  border-radius: ${theme.shape.borderRadius}px;
  font-size: ${theme.typography.caption.fontSize};
  color: ${theme.colors.onSurfaceVariant};
  line-height: ${theme.typography.caption.lineHeight};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.typography.caption.fontSize};
    padding: ${theme.spacing(1)};
  }
`;

const BrandingFooter = styled.div`
  text-align: center;
  margin-top: ${theme.spacing(4)};
  padding-top: ${theme.spacing(3)};
  border-top: 1px solid ${theme.colors.outline};
  color: ${theme.colors.onSurfaceVariant};
  font-size: ${theme.typography.body2.fontSize};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.typography.caption.fontSize};
  }
`;

const Footer: React.FC = () => {
  const { t } = useTranslation();

  return (
    <FooterContainer>
      <FooterContent>
        <ImportantInfo>
          <InfoTitle>💳 {t('noteCashless').split('.')[0]}</InfoTitle>
          <InfoText>{t('noteCashless')}</InfoText>
          <InfoText>{t('noteVAT')}</InfoText>
        </ImportantInfo>

        <ServiceHours>
          <ServiceTitle>🕒 Service Hours</ServiceTitle>
          <ServiceText>{t('serviceHours')}</ServiceText>
        </ServiceHours>

        <AllergenInfo>
          <strong>{t('allergens')}:</strong> A=Gluten, B=Crustaceans, C=Eggs, D=Fish, E=Groundnuts, F=Soybeans, G=Milk, H=Nuts, I=Celery, J=Mustard, K=Sesame, L=Sulphites, M=Lupins, N=Molluscs
          <br />
          <strong>{t('additives')}:</strong> 1=Dye, 2=Preservative, 3=Antioxidant, 4=Flavour enhancer, 5=Sulphurated, 6=Blackened, 7=Phosphate, 8=Milk protein, 9=Caffeinated, 10=Quinine, 11=Sweeteners, 12=Waxed
          <br /><br />
          {t('priceNote')} {t('selectionNote')}
        </AllergenInfo>

        <BrandingFooter>
          <strong>SJ EuroNight</strong> in cooperation with RDC Deutschland
          <br />
          Schnell zusammengebaut von den Nachtzug-Freunden bei <a href="https://grossartig.io" target="_blank" rel="noopener noreferrer">grossartig.io</a>
          <br />
          Achtung: Diese Speisekarte ist ein fiktives Beispiel und dient nur zu Illustrationszwecken.
        </BrandingFooter>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;